import { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Image, Trash2, X } from 'lucide-react';
import {
    createProject, updateProject, deleteProjectFile, getPhases, getProject,
    type ProjectFile, type Project, type Phase,
} from '../../projectsService';
import { useFormSave } from '../../core/data';
import Modal from '../../core/ui/Modal';
import Button from '../../core/ui/Button';
import FormField from '../../core/ui/FormField';
import { ButtonSpinner } from '../../core/ui/ButtonSpinner';
import './ProjectEditModal.css';

type ProjectEditModalProps = {
    mode: 'create' | 'edit';
    projectId?: string | undefined;
    name?: string | undefined;
    description?: string | undefined;
    files?: ProjectFile[] | undefined;
    phase?: number | null | undefined;
    stage?: number | null | undefined;
    onClose: () => void;
    onSaved: (project: { id: string; name: string; description: string; files: ProjectFile[]; phase?: number | null; stage?: number | null }) => void;
};

export default function ProjectEditModal({
    mode, projectId, name = '', description = '', files = [], phase = null, stage = null, onClose, onSaved,
}: ProjectEditModalProps) {
    const [editName, setEditName] = useState(name);
    const [editDescription, setEditDescription] = useState(description);
    const [currentFiles, setCurrentFiles] = useState<ProjectFile[]>(files);
    const [newFiles, setNewFiles] = useState<File[]>([]);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
    const [phases, setPhases] = useState<Phase[]>([]);
    const [selectedPhase, setSelectedPhase] = useState<number | null>(phase);
    const [selectedStage, setSelectedStage] = useState<number | null>(stage);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        getPhases().then(setPhases).catch(() => {});
    }, []);

    useEffect(() => {
        if (mode === 'edit' && projectId) {
            getProject(projectId).then((proj) => {
                setSelectedPhase(proj.phase ?? null);
                setSelectedStage(proj.stage ?? null);
            }).catch(() => {});
        }
    }, [mode, projectId]);

    const currentPhaseStages = phases.find((p) => p.id === selectedPhase)?.stages ?? [];

    const handlePhaseChange = (value: string) => {
        const id = value === '' ? null : Number(value);
        setSelectedPhase(id);
        setSelectedStage(null);
    };

    const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setNewFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const handleRemoveNewFile = (index: number) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDeleteExistingFile = async (fileId: string) => {
        setDeletingFileId(fileId);
        try {
            await deleteProjectFile(fileId);
            setCurrentFiles((prev) => prev.filter((f) => f.id !== fileId));
        } catch (error) {
            console.error('Failed to delete file:', error);
        }
        setDeletingFileId(null);
    };

    const { submit: runSave, isSaving: saving } = useFormSave<void, Project>({
        save: async () => {
            if (mode === 'create') {
                return createProject(
                    editName.trim(),
                    editDescription.trim(),
                    newFiles.length > 0 ? newFiles : undefined,
                    selectedPhase,
                    selectedStage,
                );
            }
            return updateProject(
                projectId!,
                editName.trim(),
                editDescription.trim(),
                newFiles.length > 0 ? newFiles : undefined,
                selectedPhase,
                selectedStage,
            );
        },
        successToast: mode === 'create' ? 'Project created' : 'Project saved',
        onSuccess: (result) => {
            onSaved({
                id: result.id,
                name: result.name,
                description: result.description,
                files: result.files || currentFiles,
                phase: result.phase ?? null,
                stage: result.stage ?? null,
            });
            onClose();
        },
    });

    const handleSave = () => {
        if (!editName.trim()) return;
        void runSave();
    };

    const getFileIcon = (fileType: string) =>
        fileType.startsWith('image/') ? <Image size={16} /> : <FileText size={16} />;

    const title = mode === 'create' ? 'New Project' : 'Edit Project';

    return (
        <Modal
            open
            onClose={saving ? () => {} : onClose}
            size="md"
            ariaLabel={title}
            closeOnBackdrop={!saving}
            closeOnEscape={!saving}
        >
            <Modal.Header {...(saving ? {} : { onClose })}>{title}</Modal.Header>
            <Modal.Body>
                <FormField label="Project Name" htmlFor="project-edit-name">
                    <input
                        id="project-edit-name"
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Project name"
                        className="ui-input"
                    />
                </FormField>

                <FormField label="Description" htmlFor="project-edit-desc">
                    <textarea
                        id="project-edit-desc"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Describe this project..."
                        className="ui-textarea"
                        rows={3}
                    />
                </FormField>

                <FormField label="Phase" htmlFor="project-edit-phase">
                    <select
                        id="project-edit-phase"
                        className="ui-select"
                        value={selectedPhase ?? ''}
                        onChange={(e) => handlePhaseChange(e.target.value)}
                    >
                        <option value="">Unknown</option>
                        {phases.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </FormField>

                <FormField label="Stage" htmlFor="project-edit-stage">
                    <select
                        id="project-edit-stage"
                        className="ui-select"
                        value={selectedStage ?? ''}
                        disabled={selectedPhase === null}
                        onChange={(e) => setSelectedStage(e.target.value === '' ? null : Number(e.target.value))}
                    >
                        <option value="">Unknown</option>
                        {currentPhaseStages.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </FormField>

                {currentFiles.length > 0 && (
                    <FormField label="Files">
                        <div className="file-list">
                            {currentFiles.map((file) => (
                                <div key={file.id} className="file-item">
                                    <span className="file-icon">{getFileIcon(file.file_type)}</span>
                                    <span className="file-name">{file.original_name}</span>
                                    <button
                                        type="button"
                                        className="file-delete"
                                        onClick={() => handleDeleteExistingFile(file.id)}
                                        disabled={deletingFileId === file.id}
                                        aria-label={`Remove ${file.original_name}`}
                                    >
                                        {deletingFileId === file.id ? '...' : <Trash2 size={14} />}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </FormField>
                )}

                {newFiles.length > 0 && (
                    <FormField label="New Files">
                        <div className="file-list">
                            {newFiles.map((file, i) => (
                                <div key={i} className="file-item file-item--new">
                                    <span className="file-icon"><FileText size={16} /></span>
                                    <span className="file-name">{file.name}</span>
                                    <button
                                        type="button"
                                        className="file-delete"
                                        onClick={() => handleRemoveNewFile(i)}
                                        aria-label={`Remove ${file.name}`}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </FormField>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleAddFiles}
                    className="form-hidden-file"
                    aria-label="Attach project files"
                />
                <button
                    type="button"
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Upload size={16} />
                    Add Files
                </button>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSave}
                    disabled={saving || !editName.trim()}
                >
                    {saving && <ButtonSpinner size="sm" />}
                    {saving
                        ? mode === 'create' ? 'Creating' : 'Saving'
                        : mode === 'create' ? 'Create' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
